<template>
  <div class="main-cont saved-builds-page">
    <header class="saved-builds-header">
      <div>
        <div class="saved-builds-kicker">Build Library</div>
        <h4>
          My Builds
          <span v-if="getUserBuilds && getUserBuilds !== null">({{ getUserBuilds.length }})</span>
        </h4>
        <p>
          Review saved part lists, open a build back in the builder, and purchase parts one listing at a time.
        </p>
      </div>

      <div class="purchase-note">
        <span class="icon icon-info-with-circle"></span>
        <span>
          GixCore saves a parts list, not a prebuilt PC. Amazon links open individual parts, so verify seller, shipping, warranty, and quantities before ordering.
        </span>
      </div>
    </header>

    <div v-if="ifBuildsLoaded" class="saved-builds-list">
      <BuildSummaryCard
        v-for="build in enrichedBuilds"
        :key="build.raw.id"
        :build="build"
        :total-parts="requiredPartCount"
        :status="build.status"
        :status-tone="build.complete ? 'complete' : 'warning'"
        :current="clickedBuild == build.raw.id"
        :expanded="isExpanded(build.id)"
        :show-prices="showPrices"
        show-performance
        variant="library"
        :action-label="'Select ' + build.name"
        @activate="selectBuild(build.raw.id)"
      >
        <template #actions>
          <div class="saved-build-actions">
            <button type="button" class="gc-button saved-build-open" @click.stop="openBuild(build.raw)">
              <span class="icon icon-edit"></span>
              Open in builder
            </button>
            <button type="button" class="saved-build-icon-button" title="Delete build" aria-label="Delete build" @click.stop="confirmRemove(build.raw.id)">
              <span class="icon icon-trash"></span>
            </button>
          </div>
        </template>

        <button
          type="button"
          class="saved-build-toggle"
          :aria-expanded="String(isExpanded(build.id))"
          @click.stop="toggleDetails(build.id)"
        >
          <span class="saved-build-toggle__icon icon icon-shopping-cart" aria-hidden="true"></span>
          <span>{{ detailsToggleLabel(build) }}</span>
          <span class="saved-build-toggle__hint">
            {{ build.complete ? 'Individual retailer links' : 'Finish selecting parts in the builder' }}
          </span>
          <span class="saved-build-toggle__caret" :class="{ 'is-open': isExpanded(build.id) }" aria-hidden="true"></span>
        </button>

        <template v-if="isExpanded(build.id)">
          <div v-if="build.chips.length" class="saved-build-chips">
            <span v-for="(chip, index) in build.chips" :key="index" class="saved-build-chip">{{ chip }}</span>
          </div>

          <!-- A complete saved build gets the buy surface; an incomplete one keeps
               the plain parts table until the missing slots are filled. -->
          <div v-if="build.complete" class="saved-build-buy" @click.stop>
            <BuyBuildPanel
              :rows="build.parts"
              :compatibility="build.compatibility"
              :power="build.power"
              :performance="build.performance"
              :total="build.totalCost"
              :build-id="build.id"
              :build-name="build.name"
              source="my_builds"
            />
          </div>

          <div v-else class="parts-panel" :class="{ 'prices-hidden': !showPrices }">
            <div class="parts-panel-head">
              <span>Part</span>
              <span>Details</span>
              <span>Qty</span>
              <span>{{ showPrices ? 'Price' : 'Availability' }}</span>
              <span>Amazon</span>
            </div>

            <div
              v-for="part in build.parts"
              :key="part.key"
              class="part-row"
              :class="{ 'part-row--missing': !part.item, 'part-row--unavailable': part.unavailable }"
            >
              <div class="part-category">
                <span class="part-category-icon icon" :class="part.icon"></span>
                <span>{{ part.label }}</span>
                <span v-if="part.qty > 1" class="part-qty-badge">×{{ part.qty }}</span>
              </div>
              <div class="part-detail">
                <strong>{{ part.name }}</strong>
                <span v-if="part.unavailable" class="part-unavailable-warning">
                  <span class="icon icon-warning"></span>
                  Unavailable on Amazon
                </span>
                <span v-else-if="part.summary">{{ part.summary }}</span>
                <span v-else-if="!part.item">Not selected</span>
              </div>
              <div class="part-qty"><span v-if="part.qty > 1">×{{ part.qty }}</span></div>
              <div class="part-price">
                <span v-if="part.unavailable" class="part-price-unavailable">Unavailable</span>
                <template v-else-if="showPrices && part.hasPrice">
                  <strong><span class="money-muted">$</span>{{ part.price }}</strong>
                  <span v-if="part.qty > 1">{{ part.qty }} x ${{ part.unitPrice }}</span>
              </template>
              <span v-else>Check Amazon</span>
            </div>
            <div class="part-link">
              <a
                v-if="part.amazonUrl"
                :href="part.amazonUrl"
                target="_blank"
                rel="noopener noreferrer"
                @click.stop="trackAmazonClick(part)"
              >
                <span class="icon icon-shopping-cart"></span>
                {{ part.unavailable ? 'Check Amazon' : 'View on Amazon' }}
              </a>
              <span v-else style="align-content: center;" class="part-link--disabled">No ASIN</span>
            </div>
          </div>
        </div>

        <div class="build-next-step">
          <span class="icon icon-tools"></span>
          <span>
            Before ordering, make sure you have thermal paste if needed, a screwdriver, a display cable, and a licensed operating system or install media.
          </span>
        </div>
        </template>

        <div v-if="removeBuild && build.raw.id == clickedBuild" class="delete-confirm" @click.stop>
          <span>Delete this saved build?</span>
          <div>
            <button v-if="!ifProcessing" type="button" class="gc-button confirm-delete" @click="deleteBuild(build.raw.id)">Delete</button>
            <button v-if="ifProcessing" type="button" class="gc-button confirm-delete">Deleting</button>
            <button type="button" class="gc-button confirm-cancel" @click="cancelRemove()">Cancel</button>
          </div>
        </div>
      </BuildSummaryCard>

      <div v-if="!enrichedBuilds.length" class="empty-builds">
        <span class="icon icon-tools"></span>
        <h5>No saved builds yet</h5>
        <p>Start in the builder, choose your parts, then save the build here for review.</p>
      </div>
    </div>

    <LoadingOverlay v-else mode="block" min-height="360px" />
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import LoadingOverlay from '../widgets/LoadingOverlay.vue'
import BuyBuildPanel from '../builder/BuyBuildPanel.vue'
import BuildSummaryCard from '../builder/BuildSummaryCard.vue'
import { BUILD_PARTS } from '../../partOrder'
import { amazonProductUrl, SHOW_PRICES } from '../../config'
import { functions } from '../../functions'
import analytics from '../../services/analytics'
import { clockLabel, rowsFromSavedBuild, summarizeRows } from '../../utils/buildParts'

export default {
  name: "MyBuild",
  components: {
    LoadingOverlay,
    BuyBuildPanel,
    BuildSummaryCard
  },
  data() {
    return {
      removeBuild: false,
      clickedBuild: '',
      lastFetchedUserId: null,
      // Build id -> parts list open. Collapsed until asked for.
      expandedBuilds: {},
    };
  },
  computed: {
    ...mapGetters([
      "getUser",
      'getUserBuilds',
      'ifProcessing',
      'ifBuildsLoaded',
    ]),
    enrichedBuilds(){
      return (this.getUserBuilds || []).map((build) => this.enrichBuild(build))
    },
    showPrices(){
      return SHOW_PRICES
    },
    requiredPartCount(){
      return BUILD_PARTS.length
    },
  },
  watch: {
    getUser: {
      immediate: true,
      handler(user){
        this.fetchBuildsForUser(user)
      }
    }
  },
  methods: {
    ...mapActions([
      'fetchUserBuilds',
      'rmBuild',
      'loadBuild'
    ]),
    resolveUserId(user){
      if(user && user.id){ return user.id }
      if(typeof user == 'string'){
        try {
          var parsedUser = JSON.parse(user)
          if(parsedUser && parsedUser.id){ return parsedUser.id }
        } catch (e) {
          return null
        }
      }
      if(localStorage.user){
        try {
          var localUser = JSON.parse(localStorage.user)
          if(localUser && localUser.id){ return localUser.id }
        } catch (e) {
          return null
        }
      }
      return null
    },
    fetchBuildsForUser(user){
      var userId = this.resolveUserId(user)
      if(!userId || this.lastFetchedUserId == userId){ return }
      this.lastFetchedUserId = userId
      this.fetchUserBuilds(userId)
    },
    enrichBuild(build){
      var parts = rowsFromSavedBuild(build)
      var summary = summarizeRows(parts)
      var performance = this.performanceScore(build)
      var status = this.buildStatus(build)
      var unavailable = parts.filter((part) => part.unavailable)
      var selectedCount = parts.filter((part) => part.item).length
      return {
        raw: build,
        id: build.id,
        name: build.name || 'Saved build',
        complete: status.complete,
        status: status.label,
        statusClass: status.className,
        missingRequiredText: status.missingText,
        image: this.buildImage(build),
        parts: parts,
        partCount: parts.filter((part) => part.item).reduce((count, part) => count + part.qty, 0),
        selectedCount: selectedCount,
        progress: this.requiredPartCount ? Math.round(selectedCount / this.requiredPartCount * 100) : 0,
        unavailableCount: unavailable.reduce((count, part) => count + part.qty, 0),
        missingCount: summary.missingPriceCount,
        hasPrice: summary.pricedItemCount > 0,
        totalPrice: this.formatMoney(summary.totalCost),
        totalCost: summary.totalCost,
        tdp: summary.tdp,
        performance: performance,
        performanceClass: this.performanceClass(performance),
        chips: this.buildChips(build, summary.tdp),
        highlights: this.buildHighlights(parts),
        // A saved build carries no live compatibility table -- the store's checks
        // describe whatever is currently open in the builder. Report only what we
        // actually know about this record: parts that are no longer purchasable.
        compatibility: {
          conflicts: 0,
          warnings: unavailable.length,
          issues: unavailable.map((part) => part.label + ': ' + part.name + ' is no longer available on Amazon.')
        },
        power: {
          watts: summary.tdp,
          wattage: this.psuWattage(build)
        }
      }
    },
    psuWattage(build){
      var psu = build && build.psu
      var wattage = parseInt(psu && psu.specs && psu.specs.wattage)
      return isNaN(wattage) || wattage <= 0 ? null : wattage
    },
    hasBuildPart(value){
      if(Array.isArray(value)){ return value.length > 0 }
      return !!value
    },
    buildStatus(build){
      var missing = BUILD_PARTS
        .filter((part) => !this.hasBuildPart(build[part.backendKey]))
        .map((part) => part.label)

      if(missing.length){
        return {
          complete: false,
          label: 'Missing ' + missing.length + ' part' + (missing.length == 1 ? '' : 's'),
          className: 'saved-build-status--warning',
          missingText: 'Missing: ' + missing.join(', ')
        }
      }

      return {
        complete: true,
        label: 'Parts list complete',
        className: 'saved-build-status--complete',
        missingText: ''
      }
    },
    clockLabel(value){
      return clockLabel(value)
    },
    buildImage(build){
      var caseImage = this.itemImage(build.case)
      if(caseImage){ return caseImage }
      if(build.image && typeof build.image == 'string' && build.image !== 'undefined' && build.image !== 'null'){
        return build.image
      }
      return ''
    },
    itemImage(item){
      if(!item){ return '' }
      if(item.mainImg){ return item.mainImg }
      if(item.imgUrl){
        try {
          var images = JSON.parse(item.imgUrl)
          if(typeof images[0] == 'string' && images[0].includes('<a href')){ return images[1] }
          return images[0]
        } catch(e) {
          return ''
        }
      }
      return ''
    },
    isExpanded(id){
      return !!this.expandedBuilds[id]
    },
    toggleDetails(id){
      this.$set(this.expandedBuilds, id, !this.expandedBuilds[id])
    },
    detailsToggleLabel(build){
      if(this.isExpanded(build.id)){ return 'Hide parts and purchase options' }
      if(build.complete){ return 'Review parts and purchase options' }
      var selected = build.parts.filter((part) => part.item).length
      return 'Show ' + selected + ' selected part' + (selected == 1 ? '' : 's')
    },
    // What someone actually calls a build by: the two parts that define it, then
    // how much memory and storage it carries. Reading the parts table to learn
    // this was the reason the table had to be open on every card.
    buildHighlights(parts){
      var byType = {}
      parts.forEach((part) => {
        if(!part.item || byType[part.type]){ return }
        byType[part.type] = part
      })
      var highlights = []
      var named = [['processor', 'CPU'], ['graphics', 'GPU']]
      named.forEach((entry) => {
        var part = byType[entry[0]]
        if(!part){ return }
        highlights.push({
          key: entry[0],
          kind: entry[1],
          name: part.qty > 1 ? part.name + ' x' + part.qty : part.name
        })
      })
      var memory = this.capacityLabel(parts, 'ram')
      if(memory){ highlights.push({ key: 'ram', kind: 'Memory', name: memory }) }
      var storage = this.capacityLabel(parts, 'storage')
      if(storage){ highlights.push({ key: 'storage', kind: 'Storage', name: storage }) }
      return highlights
    },
    // Capacity totals read better than a kit's full retail name, which is mostly
    // the same numbers spelled out at four times the length.
    capacityLabel(parts, type){
      var total = 0
      var kinds = {}
      parts.forEach((part) => {
        if(part.type != type || !part.item){ return }
        var item = part.item
        var each = type == 'ram'
          ? parseInt(item.compat && item.compat.capacity) || 0
          : parseInt(item.specs && item.specs.Capacity) || 0
        total += each * (part.qty || 1)
        var kind = type == 'ram'
          ? (item.compat && item.compat.ram_type)
          : (item.specs && item.specs.Disk_Type)
        if(kind){ kinds[String(kind).trim()] = true }
      })
      if(!total){ return '' }
      var unit = type == 'ram' ? ' GB' : ' TB'
      var names = Object.keys(kinds)
      return total + unit + (names.length == 1 ? ' ' + names[0] : '')
    },
    buildChips(build, tdp){
      var chips = []
      if(build.processor && build.processor.specs){
        var cpuClock = this.clockLabel(build.processor.specs.Core_Frequency)
        if(cpuClock){ chips.push(cpuClock) }
        if(build.processor.specs.Number_of_Cores){ chips.push(build.processor.specs.Number_of_Cores + ' cores') }
      }
      if(build.graphics && build.graphics.length && build.graphics[0].specs && build.graphics[0].specs.graphics_ram_capacity){
        chips.push(build.graphics[0].specs.graphics_ram_capacity + ' GB GPU')
      }
      if(build.motherboard && build.motherboard.compat && build.motherboard.compat.form_factor){
        chips.push(build.motherboard.compat.form_factor)
      }
      if(build.ram && build.ram.length){
        var ramCapacity = this.totalCapacity(build.ram, 'ram')
        if(ramCapacity){ chips.push(ramCapacity + ' GB RAM') }
      }
      if(build.storage && build.storage.length){
        var storageCapacity = this.totalCapacity(build.storage, 'storage')
        if(storageCapacity){ chips.push(storageCapacity + ' TB storage') }
      }
      if(tdp){ chips.push('~' + tdp + ' W') }
      return chips
    },
    totalCapacity(items, type){
      var total = 0
      items.forEach((item) => {
        if(type == 'ram'){
          total += parseInt(item.compat && item.compat.capacity) || 0
        }
        if(type == 'storage'){
          total += parseInt(item.specs && item.specs.Capacity) || 0
        }
      })
      return total
    },
    performanceScore(build){
      var perf = functions.getBuildPerformance(build, false) || {}
      return Math.min(100, Math.round(((perf.Total || 0) / 160) * 100))
    },
    performanceClass(score){
      if(score >= 100){ return 'score-high' }
      if(score >= 67){ return 'score-good' }
      if(score >= 34){ return 'score-mid' }
      return 'score-low'
    },
    getNumericPrice(value){
      return functions.parsePrice(value)
    },
    formatMoney(value){
      var price = this.getNumericPrice(value)
      if(price === null){ return '' }
      return price.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")
    },
    selectBuild(id){
      this.clickedBuild = id
    },
    openBuild(build){
      this.loadBuild(build)
    },
    confirmRemove(id){
      this.clickedBuild = id
      this.removeBuild = true
    },
    cancelRemove(){
      this.removeBuild = false
    },
    async deleteBuild(id){
      await this.rmBuild(id)
      this.removeBuild = false
    },
    trackAmazonClick(part){
      try {
        window.dataLayer = window.dataLayer || []
        window.dataLayer.push({
          event: 'affiliate_click',
          affiliate: 'amazon',
          part_type: part && part.item && part.item.type,
          asin: part && part.item && part.item.asin,
          model: part && part.item && part.item.model,
          price: SHOW_PRICES ? part && part.item && part.item.amzn_price : null,
          source: 'my_builds'
        })
      } catch (e) { /* analytics must never block navigation */ }
      // First-party audience event (was GTM-only before).
      try {
        var it = part && part.item ? part.item : {}
        analytics.trackAmazonClick({
          targetId: it.id,
          asin: it.asin,
          url: amazonProductUrl(it.asin),
          partType: it.type,
          model: it.model,
          price: SHOW_PRICES ? it.amzn_price : null,
          source: 'my_builds',
        })
      } catch (e) { /* best-effort */ }
    },
  },
};
</script>

<style scoped>
@import "../../assets/css/dashboard.css";

.saved-build-buy {
  margin-top: 0.85rem;
}

.saved-builds-page {
  width: 100%;
  margin-left: 0 !important;
  padding: 0.25rem 1rem 2.2rem !important;
  color: #cfd2da;
}

.saved-builds-header {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(320px, 430px);
  gap: 1rem;
  align-items: end;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid rgba(145, 152, 170, 0.58);
}

.saved-builds-kicker {
  color: #1997c6;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.9px;
  line-height: 1;
  text-transform: uppercase;
}

.saved-builds-header h4 {
  margin: 0.35rem 0 0.35rem;
  color: #dce2ec;
  font-size: 22px;
  font-weight: 500;
  letter-spacing: 1.5px;
  line-height: 1.2;
  text-transform: uppercase;
}

.saved-builds-header h4 span {
  color: #1997c6;
}

.saved-builds-header p {
  max-width: 760px;
  margin: 0;
  color: #98a1b4;
  font-size: 14px;
  line-height: 1.45;
}

.purchase-note {
  display: grid;
  grid-template-columns: 24px minmax(0, 1fr);
  gap: 0.65rem;
  padding: 0.35rem 0 0.35rem 0.85rem;
  border-left: 2px solid rgba(25, 151, 198, 0.62);
  color: #9ca7ba;
  font-size: 12px;
  line-height: 1.45;
}

.purchase-note .icon {
  margin-top: 2px;
  color: #1997c6;
}

.saved-builds-list {
  display: grid;
  gap: 1rem;
}

.saved-build-card {
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(77, 88, 112, 0.76);
  border-radius: 8px;
  background:
    radial-gradient(circle at 18% 0%, rgba(25, 151, 198, 0.08), transparent 31%),
    linear-gradient(135deg, rgba(18, 23, 34, 0.78), rgba(8, 11, 17, 0.74));
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.035),
    0 14px 32px rgba(0, 0, 0, 0.16);
  transition: border-color 0.18s ease, box-shadow 0.18s ease, background 0.18s ease;
}

.saved-build-card:hover,
.saved-build-card.is-selected {
  border-color: rgba(25, 151, 198, 0.62);
  background:
    radial-gradient(circle at 18% 0%, rgba(25, 151, 198, 0.11), transparent 32%),
    linear-gradient(135deg, rgba(20, 26, 38, 0.82), rgba(9, 12, 18, 0.76));
}

.saved-build-card__top {
  display: grid;
  grid-template-columns: 164px minmax(0, 1fr);
  gap: 1.1rem;
  padding: 1rem;
}

.saved-build-thumb {
  width: 100%;
  min-height: 132px;
  border: 1px solid rgba(202, 210, 224, 0.5);
  border-radius: 8px;
  background-color: #e8edf3;
  background-repeat: no-repeat;
  background-position: center;
  background-origin: content-box;
  background-size: contain;
  box-shadow:
    inset 0 0 24px rgba(17, 24, 38, 0.16),
    inset 0 0 0 1px rgba(255, 255, 255, 0.5),
    0 10px 26px rgba(0, 0, 0, 0.22);
}

.saved-build-thumb--empty {
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(8, 11, 17, 0.62);
  color: #687084;
  font-size: 38px;
}

.saved-build-main {
  min-width: 0;
}

.saved-build-title-row {
  display: flex;
  gap: 1rem;
  align-items: flex-start;
  justify-content: space-between;
}

.saved-build-title-row h3 {
  margin: 0;
  color: #dce2ec;
  font-size: 24px;
  font-weight: 500;
  line-height: 1.18;
}

.saved-build-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
  margin-top: 0.35rem;
  color: #8791a5;
  font-size: 13px;
}

.saved-build-status {
  display: inline-flex;
  align-items: center;
  min-height: 19px;
  padding: 0.08rem 0.45rem;
  border: 1px solid rgba(82, 93, 118, 0.72);
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.35px;
  line-height: 1;
  text-transform: uppercase;
}

.saved-build-status--complete {
  border-color: rgba(27, 201, 142, 0.46);
  background: rgba(27, 201, 142, 0.08);
  color: #65d5ad;
}

.saved-build-status--warning {
  border-color: rgba(197, 185, 90, 0.48);
  background: rgba(197, 185, 90, 0.08);
  color: #d1c878;
}

.saved-build-actions {
  display: flex;
  gap: 0.4rem;
  align-items: center;
  flex: 0 0 auto;
  margin-top: 0.1rem;
}

.saved-build-open {
  width: auto !important;
  min-width: 146px;
  height: 30px;
  margin: 0;
  padding: 0 0.6rem;
  display: inline-flex;
  gap: 0.35rem;
  align-items: center;
  justify-content: center;
  border-color: rgba(25, 151, 198, 0.72);
  border-radius: 6px;
  background: rgba(5, 19, 29, 0.72);
  color: #18a6d8;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0;
  line-height: 1;
  text-transform: uppercase;
}

.saved-build-open .icon {
  float: none;
  margin: 0;
  padding: 0;
  font-size: 13px;
  line-height: 1;
}

.saved-build-open:hover,
.saved-build-open:focus {
  border-color: rgba(34, 187, 236, 0.9);
  background: rgba(25, 151, 198, 0.14);
  box-shadow: none;
  color: #eef2f8;
}

.saved-build-icon-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  padding: 0;
  border: 1px solid rgba(230, 71, 89, 0.48);
  border-radius: 6px;
  background: rgba(230, 71, 89, 0.06);
  color: #d77885;
  cursor: pointer;
  font-size: 13px;
  line-height: 1;
}

.saved-build-icon-button:hover,
.saved-build-icon-button:focus {
  border-color: rgba(230, 71, 89, 0.78);
  background: rgba(230, 71, 89, 0.14);
  color: #f08c99;
}

/* The parts that identify the build, one per line beside the photo. */
.saved-build-highlights {
  display: grid;
  gap: 0.28rem;
  margin: 0.9rem 0 0;
  padding: 0;
  list-style: none;
}

.saved-build-highlights li {
  display: grid;
  grid-template-columns: 64px minmax(0, 1fr);
  gap: 0.6rem;
  align-items: baseline;
  min-width: 0;
}

.saved-build-highlight__kind {
  color: #7d879b;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.saved-build-highlight__name {
  overflow: hidden;
  color: #cfd6e3;
  font-size: 14px;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Three readings the card is judged on, inline rather than in boxes: the boxes
   were the widest thing on the card and carried the least. */
.saved-build-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem 1.5rem;
  margin-top: 0.95rem;
  padding-top: 0.75rem;
  border-top: 1px solid rgba(67, 72, 87, 0.6);
}

.saved-build-stat {
  display: flex;
  align-items: baseline;
  gap: 0.4rem;
  min-width: 0;
}

.saved-build-stat strong {
  color: #dce2ec;
  font-size: 16px;
  font-weight: 650;
  line-height: 1.15;
}

.saved-build-stat small {
  color: #8791a5;
  font-size: 12px;
  font-weight: 500;
}

.saved-build-stat > span {
  color: #8791a5;
  font-size: 11px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

/* Opens the parts list. Full width so it reads as the seam between the summary
   and the detail rather than as a button floating in the card. */
.saved-build-toggle {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  min-height: 38px;
  padding: 0.55rem 1rem;
  border: 0;
  background: linear-gradient(90deg, rgba(15, 23, 34, 0.72), rgba(8, 11, 17, 0.4));
  color: #9aa5ba;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-align: left;
  text-transform: uppercase;
  cursor: pointer;
  transition: background 0.16s ease, color 0.16s ease;
}

.saved-build-toggle__icon {
  color: #4fbfe5;
  font-size: 12px;
}

.saved-build-toggle__hint {
  margin-left: auto;
  color: #69768a;
  font-size: 9px;
  font-weight: 600;
  letter-spacing: 0.045em;
}

.saved-build-toggle:hover,
.saved-build-toggle:focus-visible {
  background: rgba(25, 151, 198, 0.09);
  color: #d5e3ee;
  outline: none;
}

.saved-build-toggle__caret {
  width: 0;
  height: 0;
  border-top: 4px solid transparent;
  border-bottom: 4px solid transparent;
  border-left: 5px solid currentColor;
  transition: transform 0.16s ease;
}

.saved-build-toggle__caret.is-open {
  transform: rotate(90deg);
}

.money-muted {
  display: inline !important;
  color: #9198aa !important;
  font-size: 0.76em !important;
  margin-right: 3px;
  text-transform: none !important;
}

.score-high,
.score-good {
  color: #1bc98e !important;
}

.score-mid {
  color: #c5b95a !important;
}

.score-low {
  color: #c95e71 !important;
}

.saved-build-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin: 0.85rem 1rem 0;
}

.saved-build-chip {
  padding: 0.18rem 0.45rem;
  border: 1px solid rgba(82, 93, 118, 0.76);
  border-radius: 6px;
  background: rgba(8, 11, 17, 0.48);
  color: #aeb5c6;
  font-size: 12px;
  line-height: 1.2;
}

.parts-panel {
  margin: 0 1rem 1rem;
  border: 1px solid rgba(67, 72, 87, 0.78);
  border-radius: 8px;
  overflow: hidden;
  background: rgba(7, 10, 15, 0.42);
}

.parts-panel-head,
.part-row {
  display: grid;
  grid-template-columns: 155px minmax(0, 1fr) 54px 128px 94px;
  gap: 0.7rem;
  align-items: center;
}

.parts-panel-head {
  padding: 0.6rem 0.8rem;
  border-bottom: 1px solid rgba(67, 72, 87, 0.78);
  color: #8791a5;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.55px;
  text-transform: uppercase;
}

.part-row {
  min-height: 58px;
  padding: 0.65rem 0.8rem;
  border-bottom: 1px solid rgba(67, 72, 87, 0.52);
}

.part-row:last-child {
  border-bottom: 0;
}

.part-row--missing {
  opacity: 0.58;
}

.part-row--unavailable {
  background: rgba(224, 192, 126, 0.035);
}

.saved-build-unavailable,
.part-detail .part-unavailable-warning {
  color: #e0c07e;
}

.saved-build-unavailable .icon,
.part-unavailable-warning .icon {
  display: inline;
  margin-right: 3px;
  color: #e0c07e;
  font-size: 11px;
}

.part-price .part-price-unavailable {
  color: #e0c07e;
  font-weight: 700;
}

.part-category {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
  color: #cfd2da;
  font-weight: 500;
}

.part-category-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: 1px solid rgba(25, 151, 198, 0.48);
  border-radius: 6px;
  background: rgba(25, 151, 198, 0.08);
  color: #1997c6;
}

.part-detail {
  min-width: 0;
}

.part-detail strong {
  display: block;
  overflow: hidden;
  color: #dce2ec;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.25;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.part-detail span {
  display: block;
  margin-top: 0.14rem;
  overflow: hidden;
  color: #8791a5;
  font-size: 12px;
  line-height: 1.3;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.part-qty {
  color: #aeb5c6;
  text-align: center;
}

.part-qty-badge {
  display: none;
}

.part-price {
  color: #aeb5c6;
  text-align: right;
}

.part-price strong {
  display: block;
  color: #d7cb51;
  font-size: 15px;
  font-weight: 600;
}

.part-price span {
  display: block;
  color: #8791a5;
  font-size: 11px;
}

.part-link {
  text-align: right;
}

.part-link a,
.part-link--disabled {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  min-width: 76px;
  height: 30px;
  padding: 0 0.6rem;
  border: 1px solid rgba(25, 151, 198, 0.72);
  border-radius: 6px;
  background: rgba(5, 19, 29, 0.72);
  color: #18a6d8;
  font-size: 12px;
  font-weight: 700;
  text-decoration: none;
  text-transform: uppercase;
  white-space: nowrap;
}

/* When prices are intentionally hidden, the availability copy duplicates the
   Amazon action. Reclaim that column for a readable, single-line CTA. */
.parts-panel.prices-hidden .parts-panel-head,
.parts-panel.prices-hidden .part-row {
  grid-template-columns: 155px minmax(0, 1fr) 54px 130px;
}

.parts-panel.prices-hidden .parts-panel-head > :nth-child(4),
.parts-panel.prices-hidden .part-price {
  display: none;
}

.part-link a:hover,
.part-link a:focus {
  border-color: rgba(34, 187, 236, 0.9);
  background: rgba(25, 151, 198, 0.14);
  color: #eef2f8;
}

.part-link--disabled {
  border-color: rgba(67, 72, 87, 0.8);
  background: rgba(8, 11, 17, 0.36);
  color: #687084;
}

.build-next-step {
  display: grid;
  grid-template-columns: 22px minmax(0, 1fr);
  gap: 0.55rem;
  margin: 0 1rem 1rem;
  padding: 0.75rem 0.85rem;
  border: 1px solid rgba(67, 72, 87, 0.68);
  border-radius: 8px;
  background: rgba(8, 11, 17, 0.46);
  color: #98a1b4;
  font-size: 13px;
  line-height: 1.45;
}

.build-next-step .icon {
  margin-top: 2px;
  color: #1997c6;
}

.delete-confirm {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 0.8rem 1rem;
  border-top: 1px solid rgba(230, 71, 89, 0.35);
  background: rgba(230, 71, 89, 0.08);
  color: #e0a3ad;
}

.delete-confirm > div {
  display: flex;
  gap: 0.5rem;
}

.confirm-delete,
.confirm-cancel {
  width: auto !important;
  min-width: 82px;
  height: 32px;
  padding: 0 0.7rem;
}

.confirm-delete {
  border-color: rgba(230, 71, 89, 0.72) !important;
  color: #f08c99 !important;
}

.empty-builds {
  position: relative;
  min-height: 360px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  border: 1px solid rgba(67, 72, 87, 0.7);
  border-radius: 8px;
  background: rgba(8, 11, 17, 0.42);
  color: #98a1b4;
  text-align: center;
}

.empty-builds .icon {
  color: #1997c6;
  font-size: 30px;
}

.empty-builds h5 {
  margin: 0.25rem 0 0;
  color: #dce2ec;
  font-size: 18px;
  font-weight: 500;
}

.empty-builds p {
  margin: 0;
  color: #8791a5;
  font-size: 14px;
}

@media (max-width: 1100px) {
  .saved-builds-header,
  .saved-build-card__top {
    grid-template-columns: 1fr;
  }

  .saved-build-thumb {
    max-width: 320px;
  }

  .parts-panel-head {
    display: none;
  }

  .part-row {
    grid-template-columns: 145px minmax(0, 1fr) 44px 116px 90px;
  }

  .parts-panel.prices-hidden .part-row {
    grid-template-columns: 145px minmax(0, 1fr) 44px 130px;
  }
}

@media (max-width: 780px) {
  .saved-builds-page {
    padding: 0.25rem 0.65rem 2rem !important;
  }

  .saved-build-title-row {
    flex-direction: column;
    align-items: stretch;
  }

  .saved-build-actions {
    flex-direction: row;
    align-items: center;
    justify-content: flex-end;
  }

  .saved-build-toggle__hint {
    display: none;
  }

  .saved-build-stats {
    gap: 0.3rem 1rem;
  }

  .saved-build-highlights li {
    grid-template-columns: 52px minmax(0, 1fr);
  }

  .part-row {
    grid-template-columns: minmax(0, 1fr) auto;
    grid-template-rows: auto auto;
    gap: 0.32rem 0.7rem;
    min-height: 0;
    padding: 0.62rem 0.72rem;
  }

  .part-category {
    grid-column: 1;
    grid-row: 1;
    gap: 0.42rem;
    font-size: 12px;
  }

  .part-category-icon {
    width: 22px;
    height: 22px;
    border-radius: 5px;
    font-size: 12px;
  }

  .part-qty {
    display: none;
  }

  .part-qty-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 24px;
    height: 18px;
    padding: 0 0.35rem;
    border: 1px solid rgba(25, 151, 198, 0.38);
    border-radius: 999px;
    background: rgba(25, 151, 198, 0.08);
    color: #56c9ef;
    font-size: 10px;
    font-weight: 700;
    line-height: 1;
  }

  .part-detail {
    grid-column: 1 / -1;
    grid-row: 2;
    padding-left: 1.98rem;
  }

  .part-detail strong {
    overflow: visible;
    font-size: 13px;
    line-height: 1.3;
    white-space: normal;
  }

  .part-detail span {
    margin-top: 0.08rem;
    font-size: 11px;
    white-space: normal;
  }

  .part-price {
    grid-column: 1 / -1;
    grid-row: 3;
    padding-left: 1.98rem;
    text-align: left;
  }

  .prices-hidden .part-price {
    display: none;
  }

  .part-link {
    grid-column: 2;
    grid-row: 1;
    align-self: center;
    text-align: right;
  }

  .part-link a,
  .part-link--disabled {
    width: auto;
    min-width: 0;
    height: 26px;
    padding: 0 0.52rem;
    border-color: transparent;
    background: transparent;
    font-size: 10px;
    text-transform: none;
  }

  .part-link a:hover,
  .part-link a:focus {
    border-color: rgba(34, 187, 236, 0.46);
    background: rgba(25, 151, 198, 0.08);
  }

  .delete-confirm {
    flex-direction: column;
    align-items: stretch;
  }
}
</style>
